import type { TranscriptBlock } from '../transport/api'

/**
 * THE LAST 500 BLOCKS OF A CONVERSATION, KEPT ON THE DEVICE — owner, 2026-08-22:
 * Clean View 的 Cache 可以在 Device 上面儲存 500 個最新 Chat blocks. 這樣每次重刷就
 * 不需要重讀一整串了.
 *
 * WHAT A REFRESH COSTS TODAY, measured on app-dev before writing this: opening
 * avg-devenv (7388 blocks) loads 81 of them, and the slowest relay call in that open
 * took 206ms. So the bytes are not the problem — the WAIT is, and so is everything the
 * person had scrolled back to, which a refresh throws away entirely.
 *
 * TWO USES, and the second is the one that saves the round trip:
 *
 *   1. paint immediately from what this device already holds
 *   2. ask the server for a DELTA from where the cache ends, not for a window —
 *      `getTranscript(session, have, …, file)` appends when `client_file == fname and
 *      base <= have <= total`, so pinning the file and passing a real `have` is what
 *      turns a reopen into "the four blocks you missed"
 *
 * INDEXEDDB, NOT localStorage. avengents-server's own note: a full replace can be
 * ~8MB (2000 blocks × 4KB), so 500 blocks is on the order of 2MB for ONE agent —
 * localStorage's whole origin budget is about 5MB and it is synchronous. This store is
 * asynchronous and off the main thread's critical path.
 *
 * THE CACHE NEVER OUTRANKS THE SERVER. `file` is the pin: a different transcript file
 * is a different conversation, and the window is dropped rather than merged. Every
 * decision below is a pure function so it can be judged without a database; the
 * database wrapper is the thin part.
 */

/**
 * Owner's number. The window kept per conversation.
 *
 * 500 → 1000 on 2026-09-03 (owner: MAX_BLOCK 應該可以開到 1000), meant to move together
 * with the opening read (useTranscriptWindow TAIL_BLOCKS) — one number for how much of
 * a conversation this app holds. The read lagged at 500 until 2026-09-11, when the
 * owner asked for the thousand again; they agree now.
 *
 * WHAT IT COSTS, at the ~4KB/block the header cites: ~4MB for one conversation, and
 * MAX_CONVERSATIONS (12) windows below means an upper bound near 48MB of IndexedDB for
 * this origin. That is well inside a browser's per-origin budget for IndexedDB (unlike
 * localStorage's ~5MB, which is why this store is IndexedDB — see the header), and the
 * prune below still evicts oldest-touched first, so the bound is a ceiling rather than
 * a steady state. 4KB is the server's own average, not a measurement of this fleet:
 * a transcript of long tool output runs larger, one of short replies smaller.
 */
export const MAX_BLOCKS = 1000
/**
 * How many conversations keep a window, oldest-touched dropped first.
 *
 * Not exported: the prune lives in `writeBlocks` and nothing outside this file has a
 * reason to know the number. The per-conversation cap above IS exported, because the
 * merge logic is judged against it in the tests.
 */
const MAX_CONVERSATIONS = 12

const DB = 'wt_cache'
const STORE = 'transcripts'
const DB_VERSION = 1

export type CachedTranscript = {
  /** The transcript file this window came from. A change means: throw it away. */
  file: string
  /** ABSOLUTE index of `blocks[0]` — not an offset into anything the client holds. */
  from: number
  /** What the server said the conversation's length was when this was stored. */
  total: number
  blocks: TranscriptBlock[]
  /** Touched-at, for the conversation cap. */
  at: number
}

/** Stable per conversation, and it must not move when a name resolves late. */
export function blockCacheKey(a: {
  hash?: string | null | undefined
  session?: string | null | undefined
  host?: string | null | undefined
  device?: string | null | undefined
}): string | null {
  if (a.hash) return `${a.hash}:${a.device ?? ''}`
  return a.session ? `${a.session}@${a.host ?? ''}` : null
}

/** Keep the NEWEST `max`, and report the absolute index the survivors start at. */
export function capBlocks(
  from: number,
  blocks: TranscriptBlock[],
  max = MAX_BLOCKS,
): { from: number; blocks: TranscriptBlock[] } {
  if (blocks.length <= max) return { from, blocks }
  const drop = blocks.length - max
  // `from` moves with the window: it is an ABSOLUTE index, so dropping the head of the
  // array without advancing it would claim the newest 500 start where the oldest did —
  // and the next `have` computed from it would ask the server for the wrong place.
  return { from: from + drop, blocks: blocks.slice(drop) }
}

/* ── the thin part: IndexedDB ─────────────────────────────────────────────── */

function open(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null)
      const req = indexedDB.open(DB, DB_VERSION)
      req.onupgradeneeded = () => {
        const db = req.result
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE)
      }
      req.onsuccess = () => resolve(req.result)
      // Private windows, blocked upgrades, disabled storage: a missing cache is a slow
      // first paint, never a broken screen.
      req.onerror = () => resolve(null)
      req.onblocked = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

export async function readBlocks(key: string | null): Promise<CachedTranscript | null> {
  if (!key) return null
  const db = await open()
  if (!db) return null
  return new Promise((resolve) => {
    try {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(key)
      req.onsuccess = () => {
        const v = req.result as CachedTranscript | undefined
        resolve(v && Array.isArray(v.blocks) && typeof v.file === 'string' ? v : null)
      }
      req.onerror = () => resolve(null)
    } catch {
      resolve(null)
    } finally {
      db.close()
    }
  })
}

export async function writeBlocks(key: string | null, value: CachedTranscript): Promise<void> {
  if (!key) return
  const db = await open()
  if (!db) return
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite')
      const store = tx.objectStore(STORE)
      store.put(value, key)
      /*
       * Prune in the same transaction. Keys are few (a dozen), so reading their
       * timestamps costs nothing next to the blocks themselves — and a cap that only
       * runs on some other page's next visit is a cap that does not exist.
       */
      const all = store.getAll()
      const keys = store.getAllKeys()
      tx.oncomplete = () => {
        const rows = (all.result ?? []) as CachedTranscript[]
        const ks = (keys.result ?? []) as IDBValidKey[]
        if (rows.length > MAX_CONVERSATIONS) {
          const order = ks
            .map((k, i) => ({ k, at: rows[i]?.at ?? 0 }))
            .sort((x, y) => x.at - y.at)
            .slice(0, rows.length - MAX_CONVERSATIONS)
          const tx2 = db.transaction(STORE, 'readwrite')
          for (const { k } of order) tx2.objectStore(STORE).delete(k)
          tx2.oncomplete = () => {
            db.close()
            resolve()
          }
          tx2.onerror = () => {
            db.close()
            resolve()
          }
          return
        }
        db.close()
        resolve()
      }
      tx.onerror = () => {
        db.close()
        resolve()
      }
    } catch {
      db.close()
      resolve()
    }
  })
}

/*
 * EVICT — the counterpart to `writeBlocks`, and the reason it exists is a bug rather
 * than symmetry. The cache is keyed by the agent's hash, and that hash is derived from
 * host + session NAME. A name can be REUSED: delete `probe1` and create `probe1` again
 * and the new agent inherits the dead one's key, hence the dead one's blocks.
 *
 * The polling path already handles this — it resets when `t.file` names a different
 * conversation. The path that did not was the one where the server answers that there
 * is no conversation at all: a 404 set an error message and left the seeded blocks on
 * screen, so a fresh agent rendered "No Claude transcript for this session" directly
 * above the previous occupant's messages. Owner, 2026-08-23: 「probe1 先前的回應消失了。
 * 但是我的輸入還留著。」— the reply was gone because it belonged to a conversation the
 * new agent has no claim on; the prompt stayed because it came from here.
 *
 * A missing key is success, not an error: the caller is asserting the entry must not
 * survive, and it not being there is that.
 */
export async function dropBlocks(key: string | null): Promise<void> {
  if (!key) return
  const db = await open()
  if (!db) return
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite')
      tx.objectStore(STORE).delete(key)
      tx.oncomplete = () => resolve()
      tx.onerror = () => resolve()
      tx.onabort = () => resolve()
    } catch {
      resolve()
    }
  })
  db.close()
}
